import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import Modal from '../../utils/Modal';
import PriceLevelSelectorInstant from '../priceLevel/PriceLevelSelectorInstant';
import Typography from '../../utils/Typography';

const AddonRoot = styled.div``;

const ImageZoomButton = styled.div`
  color: ${(props) => props.theme.PRIMARY_LINK_COLOR};
  cursor: pointer;
  text-align: center;
`;

const ItemContainer = styled.div`
  @media (min-width: ${(props) => props.theme.BREAKPOINT_MOBILE_WIDTH}px) {
    display: flex;
  }
  padding: 0 0 2rem 0;
  margin: 0 0 2rem 0;
  border-bottom: solid 1px ${(props) => props.theme.PRIMARY_GRAY};
`;

const ItemImage = styled.div`
  padding: 0;
  width: 100%;
  margin: 0 auto;
  cursor: pointer;
  img {
    width: 100%;
  }
  @media (min-width: ${(props) => props.theme.BREAKPOINT_MOBILE_WIDTH}px) {
    margin: 0 2rem 0 0;
    width: 20rem;
  }
`;

const ItemContent = styled.div`
  margin: 0 0 0 0;
  padding: 0;

  @media (min-width: ${(props) => props.theme.BREAKPOINT_MOBILE_WIDTH}px) {
    flex: 1;
  }
`;

/**
 * Component to manage and display checkout add-ons.
 *
 * This component allows users to view, select, and modify add-ons for an event during checkout.
 * It handles modal interactions, updates to selected price levels, and rendering price level selectors for add-ons.
 *
 * @component
 * @extends React.Component
 * @property {Object} props - The properties passed to the component.
 * @property {Array<Object>} props.outletEventItems - List of outlet event items, each containing details about add-ons and price levels.
 * @property {boolean} [props.showTitle=false] - Whether to display the title of each add-on.
 * @property {boolean} [props.showDescription=false] - Whether to display the description of each add-on.
 * @property {boolean} [props.showPriceLevels=false] - Whether to display the price levels for add-ons.
 * @property {boolean} [props.displayFees=false] - Whether to display fees associated with price levels.
 * @property {Array<Object>} [props.selectedPriceLevels=[]] - Pre-selected price levels for the add-ons.
 * @property {Object} props.cart - The shopping cart object containing line items and related data.
 * @property {Function} [props.handleAddOnQtyChange] - Callback invoked when the quantity of add-ons is updated.
 * @property {Object} [props.modal] - Configuration for the modal used in the component.
 *
 * @state {Array<Object>} state.selectedPriceLevels - The currently selected price levels for add-ons.
 * @state {boolean} state.modalIsVisible - Whether the modal is currently visible.
 * @state {string} state.modalTitle - The title of the modal (e.g., for the image or add-on name).
 * @state {string} state.modalImage - The image URL displayed in the modal.
 *
 * @example
 * <CheckoutAddons
 *   outletEventItems={[
 *     {
 *       Name: "Add-on 1",
 *       Description: "Description for Add-on 1",
 *       Image: "image-url.jpg",
 *       PriceLevels: [{ PriceLevelID: 1, Name: "Level 1", Price: 10 }],
 *     },
 *   ]}
 *   showTitle={true}
 *   showDescription={true}
 *   showPriceLevels={true}
 *   displayFees={false}
 *   selectedPriceLevels={[]}
 *   cart={{ lineItems: [] }}
 *   handleAddOnQtyChange={(selectedPriceLevels) =>
 *     console.log("Updated add-ons:", selectedPriceLevels)
 *   }
 * />
 *
 * @remarks
 * - Manages a modal for displaying add-on images with zoom functionality.
 * - Handles updates to the selected add-ons and their quantities.
 * - Filters cart items to display only those marked as add-ons.
 * - Uses a `PriceLevelSelectorInstant` component for managing price level selection.
 *
 * @dependencies
 * - `Modal`: Used for displaying add-on images in a modal dialog.
 * - `Typography`: For styled text rendering.
 * - `PriceLevelSelectorInstant`: For managing price level selection and quantity updates.
 */
class CheckoutAddons extends React.Component {
  state = {
    selectedPriceLevels: [],
    modalIsVisible: false,
    modalTitle: '',
    modalImage: '',
  };

  setModalIsVisible = (modalIsVisible = false) => {
    this.setState({ modalIsVisible });
  };

  componentDidMount() {
    if (Array.isArray(this.props.selectedPriceLevels)) {
      this.setState({
        selectedPriceLevels: [...this.props.selectedPriceLevels],
        possiblyDirty: false,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.modal && this.props.modal && prevProps.modal.isVisible != this.props.modal.isVisible &&
        this.state.possiblyDirty) {
      this.setState({
        possiblyDirty: false,
        selectedPriceLevels: [
          ...this.props.cart.lineItems.filter(
              (li) => li.isAddOn !== false,
          )],
      });
    }
    if (prevProps.modal && this.props.modal && prevProps.modal.isVisible != this.props.modal.isVisible) {
      this.setState({ possiblyDirty: true });
    }
    if (this.props.cart) {
      let newSelectedPriceLevels = this.props.cart.lineItems.filter(
          (li) => li.isAddOn !== false,
      );
      if (newSelectedPriceLevels.toString() !== prevProps.selectedPriceLevels.toString()) {
        this.setState({
          selectedPriceLevels: [
            ...this.props.cart.lineItems.filter(
                (li) => li.isAddOn !== false,
            )],
        });
      }
    }
  }

  updatePriceLevelQty =
      (outletEventItems) =>
          (priceLevel, inc = 1, alreadySelected) => {
            let selectedPriceLevels = [...this.state.selectedPriceLevels];
            const index = selectedPriceLevels.findIndex(
                (pl) => pl.PriceLevelID === priceLevel.PriceLevelID,
            );
            const increment = parseInt(inc, 10);

            if (index === -1) {
              let selectedPriceLevel = {
                ...priceLevel,
                oei: outletEventItems.find((oei) =>
                    oei.PriceLevels.find(
                        (pl) => pl.PriceLevelID === priceLevel.PriceLevelID,
                    ),
                ),
              };
              if (increment && increment > 0) {
                selectedPriceLevel.quantity = increment;
              } else {
                selectedPriceLevel.quantity = 0;
              }

              //selectedPriceLevel.quantity = increment;
              selectedPriceLevels.push(selectedPriceLevel);
            } else {
              let spl = selectedPriceLevels[index];
              let currentQty = parseInt(spl.quantity ?? spl.Quantity, 10);

              if (isNaN(currentQty)) {
                currentQty = 0;
              }
              let newQty = parseInt(currentQty + increment, 10);

              if (newQty < 1) {
                //&& !alreadySelected
                selectedPriceLevels.splice(index);
              } else {
                selectedPriceLevels[index].quantity = newQty;
                selectedPriceLevels[index].Quantity = newQty;
              }
            }

            this.setState({ selectedPriceLevels });
            this.props.handleAddOnQtyChange &&
            this.props.handleAddOnQtyChange(selectedPriceLevels);
          };

  render() {
    const { selectedPriceLevels } = this.state;
    const { outletEventItems, showTitle, showDescription, showPriceLevels } =
        this.props;
    if (!outletEventItems || !Array.isArray(outletEventItems)) return null;
    return (
        <AddonRoot>
          <Modal
              title={this.state.modalTitle}
              open={this.state.modalIsVisible}
              onClose={() => {
                this.setState({
                  modalTitle: '',
                  modalImage: '',
                  modalIsVisible: false,
                });
              }}
          >
            <img
                src={this.state.modalImage}
                alt={this.state.modalTitle}
                style={{ width: '100%', display: 'block' }}
            />
          </Modal>
          {outletEventItems.map((oei, key) => {
            return (
                <ItemContainer key={key}>
                  {oei.Image && (
                      <ItemImage>
                        <img
                            src={oei.Image}
                            alt={oei.Name}
                            onClick={() => {
                              this.setState({
                                modalTitle: oei.Name,
                                modalImage: oei.Image,
                                modalIsVisible: true,
                              });
                            }}
                        />
                        <ImageZoomButton
                            onClick={() => {
                              this.setState({
                                modalTitle: oei.Name,
                                modalImage: oei.Image,
                                modalIsVisible: true,
                              });
                            }}
                        >
                          + Zoom
                        </ImageZoomButton>
                      </ItemImage>
                  )}
                  <ItemContent>
                    {showTitle && <Typography tag="h4">{oei.Name}</Typography>}
                    {showDescription && (
                        <Typography
                            tag="p"
                            dangerouslySetInnerHTML={{
                              __html: oei.Description,
                            }}
                        />
                    )}
                    {showPriceLevels &&
                    oei.PriceLevels &&
                    oei.PriceLevels.length ? (
                        oei.PriceLevels.map((priceLevel) => {
                          const selectedPriceLevel = selectedPriceLevels.find(
                              (spl) =>
                                  Number(spl.PriceLevelID) ===
                                  Number(priceLevel.PriceLevelID),
                          );

                          const priceLevelAlreadySelected =
                              this.state.selectedPriceLevels.find(
                                  (spl) =>
                                      Number(spl.PriceLevelID) ===
                                      Number(priceLevel.PriceLevelID),
                              );
                          return (
                              <div
                                  key={priceLevel.PriceLevelID}
                                  name={'addon-' + priceLevel.PriceLevelID}
                              >
                                <PriceLevelSelectorInstant
                                    displayFees={this.props.displayFees}
                                    priceLevelAlreadySelected={priceLevelAlreadySelected}
                                    priceLevel={
                                      selectedPriceLevel ? selectedPriceLevel : priceLevel
                                    }
                                    updatePriceLevelQty={this.updatePriceLevelQty(
                                        outletEventItems,
                                    )}
                                />
                              </div>
                          );
                        })
                    ) : (
                        <Typography>
                          There are currently no price levels for sale
                        </Typography>
                    )}
                  </ItemContent>
                </ItemContainer>
            );
          })}
        </AddonRoot>
    );
  }
}

CheckoutAddons.propTypes = {
  outletEventItems: PropTypes.array.isRequired,
  showTitle: PropTypes.bool,
  showDescription: PropTypes.bool,
  showPriceLevels: PropTypes.bool,
  handleAddOnQtyChange: PropTypes.func,
  displayFees: PropTypes.bool,
};

CheckoutAddons.defaultProps = {
  selectedPriceLevels: [],
  outletEventItems: [],
  showTitle: true,
  showDescription: true,
  showPriceLevels: true,
  handleAddOnQtyChange: undefined,
  displayFees: false
};

export default CheckoutAddons;
